import { call, put } from 'redux-saga/effects';

import { type GeneratorResult, takeIf } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import { createOrGetPageSet } from '@pressreader/social-api';
import { type CollectionWithId, IssuePageSet } from '@pressreader/social-types';

import {
    loadIssuePageSetByIssueKeyDone,
    loadIssuePageSetByIssueKeyFailed,
    removePageSetFromCollectionsAction,
    removePageSetFromCollectionsDone,
    removePageSetFromCollectionsFailed,
    updateIssuePageSetByIdAction,
    updateIssuePageSetByIdDone,
    updateIssuePageSetByIdFailed,
    upsertIssuePageSetCollectionAction,
    upsertIssuePageSetCollectionDone,
    upsertIssuePageSetCollectionFailed,
} from '../../actions';

function* upsertIssuePageSetCollectionSaga(pageSetId: string, collections: CollectionWithId[]): GeneratorResult<void> {
    yield put(upsertIssuePageSetCollectionAction({ pageSetId, collections }));

    const { type } = yield takeIf(
        [upsertIssuePageSetCollectionDone, upsertIssuePageSetCollectionFailed],
        action => action.payload.pageSetId === pageSetId,
    );

    if (type === String(upsertIssuePageSetCollectionFailed)) {
        throw new Error('Failed to upsert page set collections');
    }
}

function* createOrGetPageSetSaga(issueKey: string, pages: number[]): GeneratorResult<IssuePageSet> {
    try {
        const pageSet = yield call(createOrGetPageSet, {
            issueKey,
            pages,
        });

        yield put(loadIssuePageSetByIssueKeyDone({ issueKey, pageSet: [pageSet] }));

        return pageSet;
    } catch (e) {
        error(e);
        yield put(loadIssuePageSetByIssueKeyFailed({ issueKey, error: e }));
        throw e;
    }
}

function* removePageSetFromCollectionsSaga(pageSetId: string) {
    yield put(removePageSetFromCollectionsAction({ pageSetId }));

    const { type } = yield takeIf(
        [removePageSetFromCollectionsDone, removePageSetFromCollectionsFailed],
        action => action.payload.pageSetId === pageSetId,
    );

    if (type === String(removePageSetFromCollectionsFailed)) {
        throw new Error('Failed to remove page set from collections');
    }
}

function* updateIssuePageSetByIdSaga(pageSetId: string, pages: number[]) {
    yield put(updateIssuePageSetByIdAction({ pageSetId, pages }));

    const action = yield* takeIf([updateIssuePageSetByIdDone, updateIssuePageSetByIdFailed], action => action.payload.pageSetId === pageSetId);

    if (action.type === String(updateIssuePageSetByIdFailed)) {
        throw new Error('Failed to update page set');
    }
}

export { createOrGetPageSetSaga, removePageSetFromCollectionsSaga, updateIssuePageSetByIdSaga, upsertIssuePageSetCollectionSaga };
